import * as i0 from '@angular/core';
import { EventEmitter, Directive, Input, Output, Component, ViewEncapsulation, ChangeDetectionStrategy, Pipe, NgModule } from '@angular/core';
import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import katex from 'katex';
import { extractMath } from 'extract-math';
import DOMPurify from 'dompurify';
import * as i1 from '@angular/platform-browser';

class KatexDirective {
    constructor(el) {
        this.el = el;
        this.error = new EventEmitter();
    }
    ngOnChanges() {
        try {
            katex.render(this.equation, this.el.nativeElement, this.options);
        }
        catch (e) {
            this.error.emit(e);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KatexDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.2.3", type: KatexDirective, selector: "[katex]", inputs: { equation: ["katex", "equation"], options: ["katexOptions", "options"] }, outputs: { error: "error" }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KatexDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[katex]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { equation: [{
                type: Input,
                args: ['katex']
            }], options: [{
                type: Input,
                args: ['katexOptions']
            }], error: [{
                type: Output
            }] } });

class KatexComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KatexComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.2.3", type: KatexComponent, selector: "rcp-katex", inputs: { equation: "equation", options: "options" }, ngImport: i0, template: `<span [katex]="equation" [katexOptions]="options"></span>`, isInline: true, dependencies: [{ kind: "directive", type: KatexDirective, selector: "[katex]", inputs: ["katex", "katexOptions"], outputs: ["error"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KatexComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'rcp-katex',
                    template: `<span [katex]="equation" [katexOptions]="options"></span>`,
                    encapsulation: ViewEncapsulation.None,
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], propDecorators: { equation: [{
                type: Input
            }], options: [{
                type: Input
            }] } });

class KatexSanitizeTextPipe {
    constructor(sanitized) {
        this.sanitized = sanitized;
    }
    transform(value) {
        const options = { ALLOWED_TAGS: ['b', 'i', 'u', 'em', 'sub', 'sup', 'span'], ALLOWED_ATTR: ['style'] };
        const sanitizedContent = DOMPurify.sanitize(value, options);
        return this.sanitized.bypassSecurityTrustHtml(sanitizedContent);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KatexSanitizeTextPipe, deps: [{ token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: KatexSanitizeTextPipe, name: "sanitizeText" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KatexSanitizeTextPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'sanitizeText'
                }]
        }], ctorParameters: () => [{ type: i1.DomSanitizer }] });
class KatexCleanMathPipe {
    transform(value) {
        return DOMPurify.sanitize(value, { ALLOWED_TAGS: [] });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KatexCleanMathPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: KatexCleanMathPipe, name: "cleanMath" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KatexCleanMathPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'cleanMath'
                }]
        }] });

class KatexParagraphComponent {
    constructor() {
        this.forceInline = false;
    }
    get segments() {
        return extractMath(this.paragraph);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KatexParagraphComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.2.3", type: KatexParagraphComponent, selector: "rcp-katex-paragraph", inputs: { paragraph: "paragraph", forceInline: "forceInline" }, ngImport: i0, template: `
    <ng-container *ngFor="let segment of segments">
      <rcp-katex *ngIf="segment.math else textTemplate"
        [equation]="segment.raw | cleanMath" [options]="{
          displayMode: forceInline ? false : segment.type === 'display'
        }">
      </rcp-katex>
      <ng-template #textTemplate>
        <span [innerHTML]="segment.value | sanitizeText"></span>
      </ng-template>
    </ng-container>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: KatexComponent, selector: "rcp-katex", inputs: ["equation", "options"] }, { kind: "pipe", type: KatexSanitizeTextPipe, name: "sanitizeText" }, { kind: "pipe", type: KatexCleanMathPipe, name: "cleanMath" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: KatexParagraphComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'rcp-katex-paragraph',
                    template: `
    <ng-container *ngFor="let segment of segments">
      <rcp-katex *ngIf="segment.math else textTemplate"
        [equation]="segment.raw | cleanMath" [options]="{
          displayMode: forceInline ? false : segment.type === 'display'
        }">
      </rcp-katex>
      <ng-template #textTemplate>
        <span [innerHTML]="segment.value | sanitizeText"></span>
      </ng-template>
    </ng-container>
  `,
                    encapsulation: ViewEncapsulation.None,
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], propDecorators: { paragraph: [{
                type: Input
            }], forceInline: [{
                type: Input
            }] } });

class ReadCubeKatexModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: ReadCubeKatexModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.2.3", ngImport: i0, type: ReadCubeKatexModule, declarations: [KatexDirective,
            KatexComponent,
            KatexParagraphComponent,
            KatexSanitizeTextPipe,
            KatexCleanMathPipe], imports: [CommonModule], exports: [KatexDirective,
            KatexComponent,
            KatexParagraphComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: ReadCubeKatexModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.3", ngImport: i0, type: ReadCubeKatexModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        KatexDirective,
                        KatexComponent,
                        KatexParagraphComponent,
                        KatexSanitizeTextPipe,
                        KatexCleanMathPipe,
                    ],
                    exports: [
                        KatexDirective,
                        KatexComponent,
                        KatexParagraphComponent
                    ],
                    providers: []
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { KatexComponent, KatexDirective, KatexParagraphComponent, ReadCubeKatexModule };
